// FLIP spring reflow for SortableGrid — the physicality layer. Whenever the
// live placement map changes (drag preview, resize preview, commit, tidy, undo,
// server-driven change), displaced tiles spring from their previous position to
// the new one instead of teleporting.
//
// Engine: the Web Animations API — nothing framework-specific.
// `node.animate()` never touches React state or the element's inline `style`,
// so it cannot fight React's own style writes, and a finished animation is
// self-cleaning (keyframes end at the identity transform with no `fill`).
// The springy feel comes from a damped spring numerically sampled ONCE at
// module load into a keyframe curve — works in every WAAPI browser, no
// reliance on the newer `linear()` easing.
//
// Design notes (verified against react-flip-toolkit and @formkit/auto-animate
// source):
//   - INTERRUPTION: measure the mid-flight visual position BEFORE cancelling
//     the running animation (the computed transform still reflects it), fold
//     the residual into the new delta, THEN cancel and start the redirected
//     spring. Cancel-first implementations visibly pop under the rapid
//     retriggering a live drag produces.
//   - BATCHING: one read pass over every tile (offsets + residual transforms),
//     then one write pass (cancel + animate). Interleaving reads and writes
//     invalidates layout per tile — O(N) thrash on large dashboards.
//   - Offsets (`offsetLeft`/`offsetTop`) are relative to the grid's
//     `position: relative` container — immune to page/container scroll between
//     reflows, unlike viewport rects.
//   - Sub-0.5px deltas are skipped (rounding noise floods animations when
//     reflows fire many times per second).

import * as React from 'react';

import type { GridLayoutItem } from './grid-layout';

export interface FlipRegistryEntry {
  node: HTMLElement;
  anim: Animation | null;
}

// Damped spring x: 0 → 1 (stiffness 300, damping 28, mass 1 → ζ ≈ 0.81:
// slight overshoot and settle — "an object with mass being pushed"). Sampled
// with semi-implicit Euler at 1ms substeps (the fixed-substep approach the
// rebound spring engine uses), recorded at 60Hz until settled.
function sampleSpring(
  stiffness: number,
  damping: number,
  mass: number,
): { samples: number[]; durationMs: number } {
  const substep = 0.001;
  const samplePeriod = 1 / 60;
  const samples: number[] = [0];
  let x = 0;
  let v = 0;
  let t = 0;
  let nextSample = samplePeriod;
  while (t < 5) {
    const accel = (-stiffness * (x - 1) - damping * v) / mass;
    v += accel * substep;
    x += v * substep;
    t += substep;
    if (t >= nextSample) {
      samples.push(x);
      nextSample += samplePeriod;
    }
    if (Math.abs(1 - x) < 0.001 && Math.abs(v) < 0.001) break;
  }
  // Land EXACTLY on identity so no residual sub-pixel transform survives.
  samples.push(1);
  return { samples, durationMs: t * 1000 };
}

const SPRING = sampleSpring(300, 28, 1);

/** Keyframes springing a tile from a (dx, dy) displacement back to identity.
 *  Samples are uniform-interval, so WAAPI's even keyframe distribution (with
 *  its default linear easing between frames) reproduces the curve exactly. */
export function springKeyframes(dx: number, dy: number): Keyframe[] {
  return SPRING.samples.map((p) => ({
    transform: `translate(${dx * (1 - p)}px, ${dy * (1 - p)}px)`,
  }));
}

// One MediaQueryList for the session. `matches` is live, so re-reading it is
// enough — there is no listener because FLIP asks at the moment it animates.
let reducedMotionQuery: MediaQueryList | null = null;
function prefersReducedMotion(): boolean {
  reducedMotionQuery ??= window.matchMedia('(prefers-reduced-motion: reduce)');
  return reducedMotionQuery.matches;
}

/** Current translate of a running FLIP animation, read from the computed
 *  transform (our animations are translate-only, so the matrix is exact). */
function residualTranslate(node: HTMLElement): { x: number; y: number } {
  const transform = getComputedStyle(node).transform;
  if (!transform || transform === 'none') return { x: 0, y: 0 };
  const matrix = new DOMMatrix(transform);
  return { x: matrix.m41, y: matrix.m42 };
}

export function useFlipReflow(options: {
  /** id → { node, anim } for every mounted tile (owned by the grid shell). */
  registry: React.MutableRefObject<Map<string, FlipRegistryEntry>>;
  /** Live (preview-aware) placement — the effect keys on it, so every reflow
   *  source funnels through here. */
  placementMap: Map<string, GridLayoutItem>;
  /** Active gesture tile (never springs — it follows the pointer/ghost). */
  activeId: string | null;
  /** Mobile stack — no grid placement, no FLIP. */
  singleColumn: boolean;
}) {
  const { registry, placementMap, activeId, singleColumn } = options;
  const prevOffsets = React.useRef(new Map<string, { left: number; top: number }>());
  // The tile active in the PREVIOUS pass also skips one pass: on drop it jumps
  // from translate-follow to its committed cell in the same frame the preview
  // merges into layout — springing it would replay the whole drag.
  const prevActiveRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (singleColumn) {
      prevOffsets.current.clear();
      prevActiveRef.current = null;
      return;
    }

    const skipIds = new Set(
      [activeId, prevActiveRef.current].filter((id): id is string => id != null),
    );
    prevActiveRef.current = activeId;
    const reduced = prefersReducedMotion();

    // Pass 1 — reads only. Residuals must be read BEFORE any cancel() below:
    // cancelling snaps the computed transform to identity and discards the
    // mid-flight position.
    const nextOffsets = new Map<string, { left: number; top: number }>();
    const moved: Array<{ entry: FlipRegistryEntry; dx: number; dy: number }> = [];
    for (const [id, entry] of registry.current) {
      const { node } = entry;
      if (!node.isConnected) continue;
      const left = node.offsetLeft;
      const top = node.offsetTop;
      nextOffsets.set(id, { left, top });
      if (skipIds.has(id)) {
        // A grabbed tile must drop any in-flight spring NOW — a running WAAPI
        // transform overrides the drag's pointer-follow translate.
        if (entry.anim) moved.push({ entry, dx: 0, dy: 0 }); // cancel-only
        continue;
      }
      const prev = prevOffsets.current.get(id);
      if (!prev) continue; // newly mounted — appear in place
      let dx = prev.left - left;
      let dy = prev.top - top;
      if (entry.anim && entry.anim.playState === 'running') {
        const residual = residualTranslate(node);
        dx += residual.x;
        dy += residual.y;
      }
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      moved.push({ entry, dx, dy });
    }
    prevOffsets.current = nextOffsets;

    // Pass 2 — writes only.
    for (const { entry, dx, dy } of moved) {
      if (entry.anim) {
        try {
          entry.anim.cancel();
        } catch {
          // cancel() can throw on detached documents
        }
        entry.anim = null;
      }
      if (dx === 0 && dy === 0) continue;
      if (reduced) continue;
      entry.anim = entry.node.animate(springKeyframes(dx, dy), {
        duration: SPRING.durationMs,
      });
    }
  }, [registry, placementMap, activeId, singleColumn]);
}
